import styled, { css } from 'styled-components'
import { colors, fonts, updatedMedia } from 'client/styles'
import { ButtonIcon } from 'src/comps/Button/styles'

export const DatePickerDesktopContainer = styled.div`
  display: none;

  ${updatedMedia.desktop} {
    display: flex;
    position: relative;
  }
`

export const DatePickerMobileContainer = styled.div`
  width: 100%;
  ${updatedMedia.desktop} {
    display: none;
  }
`

export const Header = styled.header`
  position: sticky;
  inset-block-start: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  border-block-end: 1px solid ${colors.grey4};
  background-color: ${colors.white};
  z-index: var(--z-high);
  border-start-start-radius: 8px;
  border-start-end-radius: 8px;

  ${updatedMedia.desktop} {
    display: none;
  }
`

export const TopDates = styled.div`
  background-color: ${colors.white};
  display: flex;
  padding-block: 53px 20px;
  padding-inline: 20px;
  border-start-start-radius: 8px;
  border-start-end-radius: 8px;
`

export const TopDate = styled.div`
  width: calc(50% - 0.5rem);
  background-color: ${colors.white};
  color: ${colors.grey1};
  border-radius: 3px;
  border: 2px solid ${props => (props.active ? colors.magenta : colors.grey4)};
  font-family: ${fonts.default};
  padding-block: 10px;
  padding-inline: 10px;
  line-height: 1.5;

  &:first-child {
    margin-inline-end: 1rem;
  }
`

export const DateLine = styled.span`
  display: block;
  font-weight: 700;
`

export const Ready = styled.div`
  background-color: ${colors.white};
  box-shadow: 0px -1px 2px 0px rgba(0, 0, 0, 0.15);
  inset-block-end: 0;
  inset-inline-start: 0;
  padding-block: 0.75em;
  padding-inline: 0.75em;
  position: absolute;
  width: 100%;
  z-index: var(--z-high);
  ${updatedMedia.desktop} {
    display: none;
  }
`

export const ButtonClose = styled(ButtonIcon)`
  position: absolute;
  inset-inline-end: 16px;
  inset-block-start: 18px;
  z-index: var(--z-low);
`

export const DatePickerBase = styled.div`
  padding-block: 30px 20px;
  padding-inline: 30px;
  position: absolute;
  inset-block-start: calc(100% + 10px);
  inset-inline-start: ${props => (props.type === 'default' ? '-50%' : '0')};
  border: 2px solid ${colors.grey1};
  border-radius: 8px;
  background-color: ${colors.white};
  z-index: var(--z-high);

  ${updatedMedia.onlyMobile} {
    padding-block: 0;
    padding-inline: 0;
    position: fixed;
    inset: 0;
    border: none;
    max-height: fill-available;
  }

  ${updatedMedia.onlyTablet} {
    padding-block: 0;
    padding-inline: 0;
    position: fixed;
    inset: 0;
    border: none;
  }
`

const startDate = css`
  color: var(--color-neutral-100);
  background-color: var(--color-neutral-300);
  position: relative;

  &:before {
    content: '';
    background-color: ${colors.white};
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: 0;
    position: absolute;
    width: 50%;
    z-index: var(--z-base);
  }

  span {
    background-color: var(--color-neutral-800);
    border-radius: 50%;
    z-index: var(--z-base);
  }
`

const endDate = css`
  color: var(--color-neutral-100);
  background-color: var(--color-neutral-300);
  position: relative;

  &:before {
    content: '';
    background-color: ${colors.white};
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-end: 0;
    position: absolute;
    width: 50%;
    z-index: var(--z-base);
  }

  span {
    background-color: var(--color-neutral-800);
    border-radius: 50%;
    z-index: var(--z-base);
  }
`

const disabled = css`
  pointer-events: none;

  span {
    color: ${colors.grey3};
  }
`

const selected = css`
  background-color: var(--color-neutral-300);
`

export const DatePicker = styled.div`
  overflow-y: auto;
`

export const Month = styled.div`
  align-items: center;
  display: flex;
  font-weight: 700;
  padding-block: 0 20px;
  padding-inline: 8px;
  text-align: center;

  svg {
    flex: 0 0 auto;
  }

  p {
    flex: 1 0 auto;
    line-height: 1.5;
  }
`

export const Week = styled.ul`
  display: flex;
  justify-content: space-between;
`

const baseDayStyles = css`
  flex: 1 0 auto;
  text-align: center;
  font-size: 14px;
  line-height: 1.4;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  visibility: ${props => (props.isVisible ? 'visible' : 'hidden')};
  width: 44px;
  height: 44px;

  span {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 44px;
    height: 44px;
  }

  &:hover {
    span {
      border-radius: 100%;
      border: 2px solid var(--color-neutral-800);
    }
  }
`

export const StartDate = styled.li`
  ${baseDayStyles}
  ${startDate}
`

export const EndDate = styled.li`
  ${baseDayStyles}
  ${endDate}
`

export const SelectedDate = styled.li`
  ${baseDayStyles}
  ${selected}
`

export const UnavailableDate = styled.li`
  ${baseDayStyles}
  ${disabled}
`

export const DefaultDay = styled.li`
  ${baseDayStyles}
`

export const AddMonths = styled(ButtonIcon)`
  width: 100%;
  padding-block: 15px;
  padding-inline: 0;
  box-shadow: 0px -1px 2px 0px rgba(0, 0, 0, 0.15);
`

export const WeekDays = styled.ul`
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding-block: 10px;
  padding-inline: 0;

  ${props =>
    props.id === 'small' &&
    css`
      ${updatedMedia.desktop} {
        display: none;
      }
      ${updatedMedia.onlyMobile} {
        display: flex;
      }
      ${updatedMedia.onlyTablet} {
        display: flex;
      }
    `}

  ${props =>
    props.id === 'desktop' &&
    css`
      ${updatedMedia.desktop} {
        display: flex;
      }
      ${updatedMedia.onlyMobile} {
        display: none;
      }
      ${updatedMedia.onlyTablet} {
        display: none;
      }
    `}

  li {
    flex: 1 0 auto;
    font-size: 12px;
    line-height: 1.3;
    text-align: center;
    text-transform: lowercase;
  }

  ${props =>
    props.biggerText &&
    css`
      li {
        font-size: 14px;
        line-height: 1.4;
      }
    `}
`

const horizontalVariantStyles = css`
  &:first-child {
    margin-inline-end: 20px;
  }

  ${Month} {
    background-color: ${colors.white};
  }
`

const verticalVariantStyles = css`
  ${WeekDays} {
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.15);
    min-height: 40px;
  }
`

export const CalendarWrapper = styled.div`
  border-block-end: 1px solid ${colors.grey4};
  display: flex;
  padding-block-end: 25px;
  margin-block-end: 16px;

  width: 100%;
  max-height: calc(100% - 270px);
  overflow-y: auto;
`

export const CalendarBase = styled.div`
  ${props => (props.variant === 'horizontal' ? horizontalVariantStyles : verticalVariantStyles)};
`

export const DatesResume = styled.p`
  color: ${colors.grey1};
  text-align: center;
  text-transform: lowercase;
  width: 100%;
  display: none;

  ${updatedMedia.desktop} {
    display: block;
    position: relative;
  }
`
